import React, { Component, useEffect, useRef, useState } from 'react';
import { Spinner } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import ReactCSSTransitionReplace from 'react-css-transition-replace';
import Datetime from 'react-datetime';
import fscreen from 'fscreen';
import moment from 'moment';
import useInterval from '@use-it/interval';
import _ from 'lodash';
import useTimeout from 'use-timeout';
import preload from '../../utils/preload';
import { eventSlideshowSetIndex, requestMedia, requestMedias, resetSlideshowQueue } from '../../actions/actions';
import { CloseIcon, FullscreenIcon } from '../Icons';
import { getVideoPlayback } from './slideshowTiming';

const EventSlideshow = (props) => {
  const { onClose = () => {}, slideDuration = 5000, fetchInterval = 5000 } = props;

  const dispatch = useDispatch();
  const {
    isLoading, // Media loading state
    slideIndex, // Index of the current slide in the queue
    data: queue, // Queue of slides to play
  } = useSelector((store) => store.eventSlideshowQueue);

  // Object containing all fetched captures, keyed by mediaId
  const photos = useSelector((store) => store.photos);
  // The current event ID
  const eventId = useSelector((store) => store.activeEvent?.eventId);
  // Capture Kinds to show
  const captureKinds = useSelector((store) => store.activeEvent?.liveGallery.displayCaptureKinds);
  const events = useSelector((store) => store.events);

  // Slideshow playing state
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoLoop, setVideoLoop] = useState(false);

  // Cap consecutive auto-skips so a run of broken captures pauses instead of flickering.
  const skipCountRef = useRef(0);
  const MAX_CONSECUTIVE_SKIPS = 3;
  const advanceTimerRef = useRef(null);

  const localRequestMedia = () => {
    const filter = {
      filterByCaptureMode: false,
      filterByTime: false,
      isMobile: false,
      order: 'DESC',
      captureKind: captureKinds,
    };
    dispatch(requestMedias(eventId, null, null, filter));
  };

  // Capture to render for the current slide
  const currentCapture = photos[queue[slideIndex]?.mediaId];
  const isCurrentVideo = currentCapture?.type === 'video';
  const isSoloVideo = isCurrentVideo && queue.length <= 1;
  const isCaptureLoaded = !!currentCapture;

  // Go to the next slide
  const goToNext = async () => {
    let newIndex = slideIndex + 1;
    // Reset to 0 when we reach the end
    if (newIndex >= queue.length) {
      newIndex = 0;
    }
    // Skip entries whose photo data hasn't loaded yet (max 5 skips to prevent infinite loop)
    let skips = 0;
    while (!photos[queue[newIndex]?.mediaId] && skips < 5) {
      newIndex = (newIndex + 1) % queue.length;
      skips += 1;
    }
    // Set new index in store
    dispatch(eventSlideshowSetIndex(newIndex));
    // If we are nearing the end of the queue, load another page of captures
    if (newIndex >= queue.length - 1) {
      localRequestMedia();
    }
  };

  // Always call the freshest goToNext from deferred timers (avoids stale closures).
  const goToNextRef = useRef(goToNext);
  goToNextRef.current = goToNext;

  const clearAdvanceTimer = () => {
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  };

  const handleVideoEnded = () => {
    goToNext();
  };

  const handleVideoError = () => {
    skipCountRef.current += 1;
    if (skipCountRef.current <= MAX_CONSECUTIVE_SKIPS) {
      goToNext();
    }
  };

  const handleImageLoad = () => {
    skipCountRef.current = 0;
  };

  const handleVideoLoadedMetadata = (e) => {
    skipCountRef.current = 0;
    const durationMs = (e?.target?.duration || 0) * 1000;
    const { loop, advanceAfterMs } = getVideoPlayback(durationMs, { isSolo: isSoloVideo });
    setVideoLoop(loop);
    clearAdvanceTimer();
    if (advanceAfterMs != null) {
      advanceTimerRef.current = setTimeout(() => goToNextRef.current(), advanceAfterMs);
    }
  };

  // Images advance on this timer; videos self-schedule from onLoadedMetadata.
  useEffect(() => {
    clearAdvanceTimer();
    setVideoLoop(false);
    if (!isPlaying || !isCaptureLoaded || isSoloVideo) {
      return undefined;
    }
    if (isCurrentVideo) {
      return clearAdvanceTimer;
    }
    advanceTimerRef.current = setTimeout(() => goToNextRef.current(), slideDuration);
    return clearAdvanceTimer;
  }, [slideIndex, isPlaying, isCurrentVideo, isSoloVideo, isCaptureLoaded, slideDuration]);

  // Keep track of the last time we polled for new captures
  const [lastFetchedTime, setLastFetchedTime] = useState(moment());
  // Fetch newly-uploaded captures since the lastFetchedTime
  const fetchNewCaptures = () => {
    const now = moment();
    dispatch(
      requestMedias(eventId, null, null, {
        dateFrom: lastFetchedTime,
        dateTo: now,
        order: 'ASC',
        captureKind: captureKinds,
      })
    );
    setLastFetchedTime(now);
  };
  // While playing, check for newly-uploaded captures at an interval (fetchInterval)
  useInterval(fetchNewCaptures, isPlaying ? fetchInterval : null);

  // Whether the slideshow control buttons are currently visible
  const [controlsAreVisible, setControlsAreVisible] = useState(true);
  // Fade controls after a timeout
  const [controlFadeTimer, setControlFadeTimer] = useState(null);
  useTimeout(() => setControlsAreVisible(false), controlFadeTimer > 0 ? controlFadeTimer : null);

  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    if (fscreen.fullscreenEnabled) {
      if (fscreen.fullscreenElement !== null) {
        fscreen.exitFullscreen();
      } else {
        fscreen.requestFullscreen(document.documentElement);
      }
    }
  };

  // Play the slideshow
  const play = () => {
    if (!queue.length) {
      localRequestMedia();
    }
    setIsPlaying(true);
  };

  // Start playing on mount
  useEffect(() => {
    const event = events[eventId];
    if (event?.eventMedias?.length) {
      const sorted = _.orderBy(event.eventMedias, ['date', 'mediaId'], ['desc', 'desc']);
      dispatch(resetSlideshowQueue(sorted));
    }

    play();

    // Show the controls when the mouse is moved
    const handleMouseMove = () => {
      setControlsAreVisible(true);
      setControlFadeTimer(2000);
    };
    window.addEventListener('mousemove', handleMouseMove, false);

    // Close the slideshow when the Escape key is pressed
    const handleKeyDown = (event) => {
      const { code } = event;
      if (!code) {
        return;
      }
      switch (code) {
        case 'Escape':
          onClose();
          break;
        default:
      }
    };
    window.addEventListener('keydown', handleKeyDown, false);

    return () => {
      // Remove event listeners on unmount
      window.removeEventListener('mousemove', handleMouseMove, false);
      window.removeEventListener('keydown', handleKeyDown, false);
    };
  }, []);

  // Preload captures before they are displayed
  useEffect(() => {
    const nextIndex = slideIndex + 1;
    const nextCapture = photos[queue[nextIndex]?.mediaId];
    if (!nextCapture || !nextCapture.original) {
      return;
    }
    preload(nextCapture.original);
  }, [queue, slideIndex]);

  const renderCapture = (media) => {
    if (!media || !media.type || !media.original) {
      return null;
    }
    switch (media.type) {
      case 'image':
        return <img src={media.original} alt="" onLoad={handleImageLoad} />;
      case 'video':
        return (
          <video
            autoPlay
            loop={videoLoop}
            muted={false}
            playsInline
            disableRemotePlayback={true}
            src={media.original}
            type="video/mp4"
            onLoadedMetadata={handleVideoLoadedMetadata}
            onEnded={isSoloVideo ? undefined : handleVideoEnded}
            onError={handleVideoError}
          ></video>
        );
      default:
        return null;
    }
  };

  return (
    <div className="slideshow">
      <div className={['slideshow__btns', !controlsAreVisible && 'slideshow__btns--hidden'].join(' ')}>
        <button className="slideshow__btn slideshow__btn--fullscreen" onClick={toggleFullscreen}>
          <FullscreenIcon color="#ffffff" />
          <span className="sr-only">Toggle Fullscreen</span>
        </button>

        <button className="slideshow__btn slideshow__btn--close" onClick={onClose}>
          <CloseIcon color="#ffffff" />
          <span className="sr-only">Close</span>
        </button>
      </div>

      <div className="slideshow__inner">
        {currentCapture ? (
          <ReactCSSTransitionReplace
            transitionName="cross-fade"
            transitionEnterTimeout={1000}
            transitionLeaveTimeout={1000}
          >
            <div key={`slide_${currentCapture.id}`} className="slideshow__slide">
              {renderCapture(currentCapture)}
            </div>
          </ReactCSSTransitionReplace>
        ) : (
          <div className="slideshow__placeholder">
            <h2>No Captures Yet</h2>
            <p>Media will load in automatically as it is captured.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventSlideshow;
